import React, { useContext } from "react";
import {
  Plan,
  BenefitItemProps,
  DrugCostInfo,
  PlanDetailsSection,
} from "../../@types";
import {
  ItemIterator,
  PlanDetailSection,
  PlanDetailListWithHelpDrawer,
  BenefitItem,
} from "../../components";
import { makeBenefitItemItems as mBII, useTranslate } from "../../helpers";
import MyDrugs from "./MyDrugs";
import DrugCostsTables from "./DrugCostsTables";
import OtherDrugInfo from "./OtherDrugInfo";
import Pharmacies from "./Pharmacies";
import { AbstractBenefits } from "./AbstractBenefits";
import { hasAbstractBenefits } from "./AbstractBenefits/AbstractBenefitsHelpers";
import { linkToPrescriptionCostHelp } from "../../helpers/CONSTANTS";
import { AppContext } from "../../app/store";

const DrugCoverage: React.FunctionComponent<{
  plan: Plan;
  drugCostInfo: DrugCostInfo;
}> = ({ plan, drugCostInfo }) => {
  const makeBenefitItemItems = (
    items: Array<{ label: string | JSX.Element; service?: string }>,
    benefit: string
  ): BenefitItemProps[] => mBII(items, benefit, plan);

  const t = useTranslate();
  const {
    state: { language, prescriptions },
  } = useContext(AppContext);

  return (
    <div>
      <PlanDetailSection
        id={PlanDetailsSection.COSTS_BY_DRUG_TIER}
        title={t("plan_terms.drug_coverage_and_costs")}
      >
        <div className="ds-u-margin-y--2">
          {t("plan_details.options_to_reduce")}
          <a
            className="ds-u-color--secondary"
            target="_blank"
            href={linkToPrescriptionCostHelp(language)}
            rel="noopener noreferrer"
          >
            {t("plan_details.reduce_drug_cost_link")}
          </a>
        </div>

        {hasAbstractBenefits(plan) && <AbstractBenefits plan={plan} />}

        {!!prescriptions.length && <Pharmacies drugCostInfo={drugCostInfo} />}

        {!!drugCostInfo.costs.length && (
          <>
            <DrugCostsTables plan={plan} drugCostInfo={drugCostInfo} />

            <OtherDrugInfo plan={plan} drugCostInfo={drugCostInfo} />
          </>
        )}

        <MyDrugs />

        <PlanDetailListWithHelpDrawer
          id={PlanDetailsSection.PART_B_DRUGS}
          titleId="plan_terms.medicare_part_b_drugs"
          helpId="plan_terms.medicare_part_b_drugs.definition"
          tealiumProps={{
            event_action: "Plan Details",
            event_label: "Toggle - part b drugs",
          }}
        >
          <ItemIterator Component={BenefitItem}>
            {[
              ...makeBenefitItemItems(
                [
                  {
                    label: t("plan_terms.chemotherapy"),
                    service: "chemotherapy",
                  },
                  {
                    label: t("plan_terms.other_part_b_drugs"),
                    service: "other_part_b_drugs",
                  },
                ],
                "medicare_part_b"
              ),
            ]}
          </ItemIterator>
        </PlanDetailListWithHelpDrawer>
      </PlanDetailSection>
    </div>
  );
};

export default DrugCoverage;
